import { http } from '@common/utils';
import { trackPromise } from 'react-promise-tracker';
import { ServiceHelper } from './serviceHelper';
import {
  IPhotoRequest,
  IVeterinarianModel,
  VeterinarianModel,
} from '@common/models';
import { ServiceMethodOptions } from './types';
import { VetScheduleRequest } from '@common/models/requests';

export class VeterinarianService {
  private static baseUrl: string = '/api/veterinarian';

  public static tags = {
    getVeterinarianByVetId: 'getVeterinarianByVetId',
    getVeterinarianByUserId: 'getVeterinarianByUserId',
    getVeterinarianByStateId: 'getVeterinarianByStateId',
    saveSchedulePreferences: 'saveSchedulePreferences',
    addVeterinarianClinicSignature: 'addVeterinarianClinicSignature',
    deleteVeterinarianClinicSignature: 'deleteVeterinarianClinicSignature',
    saveVeterinarianSignature: 'saveVeterinarianSignature',
    deleteVeterinarianSignature: 'deleteVeterinarianSignature',
    saveVeterinarian: 'saveVeterinarian',
  };

  static getVeterinarianByVetId = (vetId: number): Promise<VeterinarianModel> =>
    trackPromise(
      new Promise<VeterinarianModel>((resolve, reject) => {
        http(`${this.baseUrl}/vet/${vetId}`)
          .get<IVeterinarianModel>()
          .then((json) => resolve(new VeterinarianModel(json)))
          .catch(reject);
      }),
      this.tags.getVeterinarianByVetId,
    );

  static getVeterinarianByUserId = (
    userId: number,
  ): Promise<VeterinarianModel> =>
    trackPromise(
      new Promise<VeterinarianModel>((resolve, reject) => {
        http(`${this.baseUrl}/user/${userId}`)
          .get<IVeterinarianModel>()
          .then((json) => resolve(new VeterinarianModel(json)))
          .catch(reject);
      }),
      this.tags.getVeterinarianByUserId,
    );

  static getVeterinarianByStateId = (
    stateId: number,
  ): Promise<IVeterinarianModel[]> =>
    trackPromise(
      new Promise<IVeterinarianModel[]>((resolve, reject) => {
        http(`${this.baseUrl}/${stateId}`)
          .get<IVeterinarianModel[]>()
          .then((response) => response.map((mm) => new VeterinarianModel(mm)))
          .then(resolve)
          .catch(reject);
      }),
      this.tags.getVeterinarianByStateId,
    );

  static saveSchedulePreferences = (
    veterinarianId: number,
    scheduleRequest: VetScheduleRequest,
  ): Promise<VeterinarianModel> =>
    trackPromise(
      new Promise<VeterinarianModel>((resolve, reject) => {
        http(`${this.baseUrl}/${veterinarianId}/schedulePreferences`)
          .put<IVeterinarianModel>(scheduleRequest)
          .then((json) => resolve(new VeterinarianModel(json)))
          .catch(reject);
      }),
      this.tags.saveSchedulePreferences,
    );

  // #region Veterinarian Signatures

  static addVeterinarianClinicSignature = (
    veterinarianId: number,
    clinicId: number,
    photoModel: IPhotoRequest,
  ) =>
    trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/${veterinarianId}/clinic/${clinicId}/signature`)
          .post<void>(photoModel)
          .then(resolve)
          .catch((err) => {
            ServiceHelper.showError('Failed to add signature to clinic.');
            reject(err);
          });
      }),
      this.tags.addVeterinarianClinicSignature,
    );

  static deleteVeterinarianClinicSignature = (
    veterinarianId: number,
    clinicId: number,
    options?: ServiceMethodOptions,
  ) =>
    trackPromise(
      new Promise<boolean>((resolve, reject) => {
        http(`${this.baseUrl}/${veterinarianId}/clinic/${clinicId}/signature`)
          .delete()
          .then(() => {
            ServiceHelper.showSuccess(
              'Signature has been removed from the clinic',
              options,
            );
            resolve(true);
          })
          .catch((err) => {
            ServiceHelper.showError(
              'Failed remove signature attached to clinic.',
            );
            reject(err);
          });
      }),
      this.tags.deleteVeterinarianClinicSignature,
    );

  static saveVeterinarianSignature = (
    veterinarianId: number,
    photoRequest: IPhotoRequest,
  ): Promise<void> =>
    trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/${veterinarianId}/signature`)
          .put<void>(photoRequest)
          .then(resolve)
          .catch(reject);
      }),
      this.tags.saveVeterinarianSignature,
    );

  static deleteVeterinarianSignature = (
    veterinarianId: number,
  ): Promise<boolean> =>
    trackPromise(
      new Promise<boolean>((resolve, reject) => {
        http(`${this.baseUrl}/${veterinarianId}/signature`)
          .delete()
          .then(resolve)
          .catch(reject);
      }),
      this.tags.deleteVeterinarianSignature,
    );

  static saveVeterinarian = (
    veterinarianId: number,
    model: IVeterinarianModel,
  ): Promise<IVeterinarianModel> => {
    return trackPromise(
      new Promise<VeterinarianModel>((resolve, reject) => {
        http(`${this.baseUrl}/${veterinarianId}`)
          .put<IVeterinarianModel>(model)
          .then((response) => resolve(new VeterinarianModel(response)))
          .catch((err) => reject(err));
      }),
      this.tags.saveVeterinarian,
    );
  };

  // #endregion
}
